import type { AdmissionStage, MarkAdmission } from '../api';
import type { BadgeTone } from '../components/ui';

// P2/P3: texter för antagningens steg – för marken (AdmissionsPanel) och för jägaren (Mina köp).

export function admissionTone(stage: AdmissionStage): BadgeTone {
  switch (stage) {
    case 'applied': case 'interview': case 'contract_sent': return 'accent';
    case 'approved': case 'signed': return 'info';
    case 'member': return 'ok';
    case 'rejected': return 'danger';
    default: return 'neutral';
  }
}

/** Tid kvar tills kortreservationen släpps (D1). urgent = mindre än två dygn. */
export function reservationLeft(releaseAt: string | null | undefined, now = Date.now()): { text: string; urgent: boolean } | null {
  if (!releaseAt) return null;
  const ms = Number(releaseAt) - now;
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return { text: 'ingen tid', urgent: true };
  const h = Math.floor(ms / 3_600_000);
  const d = Math.floor(h / 24);
  const text = d >= 1 ? `${d} dygn${h % 24 ? ` ${h % 24} tim` : ''}` : h >= 1 ? `${h} tim` : `${Math.max(1, Math.round(ms / 60_000))} min`;
  return { text, urgent: ms < 48 * 3_600_000 };
}

/** Vad jägaren ser under Mina köp, och vad hen kan göra. */
export function myAdmissionText(a: Pick<MarkAdmission, 'stage' | 'interviewAt' | 'groupName' | 'validUntil' | 'signingToken'> & { amount?: number | null }): { label: string; detail: string; action: 'sign' | 'interview' | null } {
  const date = (ms: string | null) => (ms ? new Date(Number(ms)).toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' }) : '');
  // D5: ansökan utan kort (belopp 0) – inget kort att reservera eller dra.
  if (a.amount === 0) {
    switch (a.stage) {
      case 'applied': return { label: 'Väntar på antagning', detail: 'Marken går igenom din profil och ansökan.', action: null };
      case 'approved': return { label: 'Godkänd', detail: 'Du är godkänd. Avtalet kommer med mejl.', action: null };
      case 'rejected': return { label: 'Inte antagen', detail: 'Marken tackade nej den här gången.', action: null };
      case 'expired': return { label: 'Ansökan gick ut', detail: 'Ingen beslutade i tid – du kan ansöka igen.', action: null };
      default: break;
    }
  }
  switch (a.stage) {
    case 'applied': return { label: 'Väntar på antagning', detail: 'Kortet är reserverat. Marken går igenom din profil – pengarna dras först när du godkänts.', action: null };
    case 'interview': return { label: 'Intervju bokad', detail: `Videointervju ${date(a.interviewAt)}. Tryck "Gå med" när det är dags.`, action: 'interview' };
    case 'approved': return { label: 'Godkänd', detail: 'Du är godkänd och betalningen är dragen. Avtalet kommer med mejl.', action: null };
    case 'contract_sent': return { label: 'Signera avtalet', detail: 'Läs avtalet och signera, så läggs du in i gruppen.', action: a.signingToken ? 'sign' : null };
    case 'signed': return { label: 'Avtal signerat', detail: 'Du läggs strax in i gruppen.', action: null };
    case 'member': return { label: 'Antagen', detail: `Du ligger i ${a.groupName ?? 'gruppen'}${a.validUntil ? ` till ${new Date(Number(a.validUntil)).toLocaleDateString('sv-SE')}` : ''}.`, action: null };
    case 'ended': return { label: 'Avslutad', detail: 'Medlemskapet har löpt ut.', action: null };
    case 'rejected': return { label: 'Inte antagen', detail: 'Reservationen släpptes – inget drogs från kortet.', action: null };
    case 'expired': return { label: 'Reservationen släppt', detail: 'Ingen beslutade i tid – inget drogs från kortet.', action: null };
  }
}
